import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Briefcase, CheckCircle2, DollarSign, Target } from "lucide-react";

interface QualifiedLead {
  id: string;
  name: string;
  company: string;
  score: number;
  framework: string;
}

interface DealCreationStepProps {
  qualifiedLeads: QualifiedLead[];
  selectedLeadIds: string[];
  onToggleLead: (leadId: string) => void;
  productPrice: number;
  isCreatingDeals: boolean;
  onCreateDeals: () => void;
  createdDealCount: number;
  error?: string;
}

export function DealCreationStep({
  qualifiedLeads,
  selectedLeadIds,
  onToggleLead,
  productPrice,
  isCreatingDeals,
  onCreateDeals,
  createdDealCount,
  error,
}: DealCreationStepProps) {
  const totalPipelineValue = selectedLeadIds.length * productPrice;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
        <Briefcase className="h-5 w-5 text-primary" />
        <p className="text-sm">
          Create deals from {qualifiedLeads.length} qualified leads to move them into your pipeline
        </p>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
          {error}
        </div>
      )}

      {qualifiedLeads.length > 0 ? (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {qualifiedLeads.map((lead) => (
            <Card
              key={lead.id}
              className={`p-3 cursor-pointer transition-all ${
                selectedLeadIds.includes(lead.id) ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
              }`}
              onClick={() => onToggleLead(lead.id)}
            >
              <div className="flex items-center gap-3">
                <Checkbox checked={selectedLeadIds.includes(lead.id)} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{lead.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{lead.company}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    <Target className="h-3 w-3 mr-1" />
                    {lead.framework}: {lead.score}%
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    <DollarSign className="h-3 w-3" />
                    {productPrice.toLocaleString()}
                  </Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No qualified leads yet. Complete qualification first.</p>
        </div>
      )}

      {selectedLeadIds.length > 0 && (
        <Card className="p-4 bg-muted/50">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-muted-foreground">Selected Leads</p>
              <p className="text-lg font-semibold">{selectedLeadIds.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Pipeline Value</p>
              <p className="text-lg font-semibold text-primary">
                ${totalPipelineValue.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
      )}

      <Button
        onClick={onCreateDeals}
        disabled={isCreatingDeals || selectedLeadIds.length === 0}
        className="w-full"
      >
        {isCreatingDeals ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Creating Deals...
          </>
        ) : (
          <>
            <Briefcase className="h-4 w-4 mr-2" />
            Create {selectedLeadIds.length} Deals
          </>
        )}
      </Button>

      {createdDealCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 rounded-lg">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-medium">
            Created {createdDealCount} deals in your pipeline
          </span>
        </div>
      )}
    </div>
  );
}
